import React, { useCallback, useEffect, useState } from 'react';
import { apiFetch } from '../utils/api';
import '../App.css';

interface ItemAlias {
  id: number;
  aliasFrom: string;
  aliasTo: string;
  enabled: number;
  notes?: string | null;
}

const emptyForm = { aliasFrom: '', aliasTo: '', notes: '', enabled: true };

/** Product-base aliases for history/opening imports (package suffix preserved). */
const ItemAliases: React.FC = () => {
  const [aliases, setAliases] = useState<ItemAlias[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(emptyForm);

  const fetchAliases = useCallback(async () => {
    try {
      const res = await apiFetch('/api/admin/item-aliases');
      const data = await res.json();
      setAliases(Array.isArray(data.aliases) ? data.aliases : []);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to load item aliases';
      setError(message);
    }
  }, []);

  useEffect(() => {
    fetchAliases();
  }, [fetchAliases]);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (saving) return;
    setError(null);
    setSuccess(null);
    if (!form.aliasFrom.trim() || !form.aliasTo.trim()) {
      setError('Both alias from and alias to are required');
      return;
    }
    try {
      setSaving(true);
      await apiFetch('/api/admin/item-aliases', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          aliasFrom: form.aliasFrom.trim(),
          aliasTo: form.aliasTo.trim(),
          enabled: form.enabled,
          notes: form.notes.trim() || null,
        }),
      });
      setForm(emptyForm);
      setSuccess(`Saved alias "${form.aliasFrom.trim()}" → "${form.aliasTo.trim()}"`);
      await fetchAliases();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to save alias';
      setError(message);
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async (row: ItemAlias) => {
    setError(null);
    setSuccess(null);
    try {
      await apiFetch('/api/admin/item-aliases', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          aliasFrom: row.aliasFrom,
          aliasTo: row.aliasTo,
          enabled: !Number(row.enabled),
          notes: row.notes || null,
        }),
      });
      await fetchAliases();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to update alias';
      setError(message);
    }
  };

  return (
    <div className="page-container container">
      <h2 className="app-header mb-4">Item Aliases</h2>
      <p className="mb-3" style={{ color: '#FFFFFF' }}>
        Remap product base names during history and opening imports. Package type suffixes are kept.
        Example: alias from <code>Old Name</code> to <code>Canonical Name</code> maps{' '}
        <code>Old Name (Keg - 1/2 bbl)</code> → <code>Canonical Name (Keg - 1/2 bbl)</code>.
        CSV upsert: admin import mode <code>item-aliases</code> with columns alias_from, alias_to.
      </p>
      {error && <div className="alert alert-danger">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}

      <form className="mb-4" onSubmit={handleCreate}>
        <h3 className="app-header mb-3" style={{ fontSize: '1.1rem' }}>
          Add / update alias
        </h3>
        <div className="row g-2 align-items-end">
          <div className="col-md-4">
            <label className="form-label">Alias from (source base)</label>
            <input
              className="form-control"
              value={form.aliasFrom}
              onChange={(e) => setForm({ ...form, aliasFrom: e.target.value })}
              placeholder="Source product name"
              required
            />
          </div>
          <div className="col-md-4">
            <label className="form-label">Alias to (canonical base)</label>
            <input
              className="form-control"
              value={form.aliasTo}
              onChange={(e) => setForm({ ...form, aliasTo: e.target.value })}
              placeholder="Canonical product name"
              required
            />
          </div>
          <div className="col-md-2">
            <label className="form-label">Notes</label>
            <input
              className="form-control"
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
            />
          </div>
          <div className="col-md-1">
            <label className="form-label d-block">On</label>
            <input
              type="checkbox"
              checked={form.enabled}
              onChange={(e) => setForm({ ...form, enabled: e.target.checked })}
            />
          </div>
          <div className="col-md-1">
            <button className="btn btn-primary w-100" type="submit" disabled={saving}>
              {saving ? '…' : 'Save'}
            </button>
          </div>
        </div>
      </form>

      <div className="inventory-table-container">
        {aliases.length === 0 ? (
          <div className="alert alert-info">No aliases yet. Table starts empty.</div>
        ) : (
          <table className="inventory-table table table-striped">
            <thead>
              <tr>
                <th>From</th>
                <th>To</th>
                <th>Notes</th>
                <th>Enabled</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {aliases.map((row) => (
                <tr key={row.id}>
                  <td>{row.aliasFrom}</td>
                  <td>{row.aliasTo}</td>
                  <td>{row.notes || '—'}</td>
                  <td>{Number(row.enabled) ? 'Yes' : 'No'}</td>
                  <td>
                    <button
                      className="btn btn-danger btn-sm"
                      type="button"
                      onClick={() => handleToggle(row)}
                    >
                      {Number(row.enabled) ? 'Disable' : 'Enable'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default ItemAliases;
